import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {snakeToTitleCase} from '@afriex/core/utils';

import {Button, CheckBox} from '@/components';
import Modal from '@/components/Modals';
import {cn} from '@/utils/cn';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';

export type RequestAdditionalDocumentsModalProps<T extends string> = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  options: readonly T[];
  formatLabel?: (option: T) => string;
  onSubmit: (selected: T[]) => Promise<void>;
  successMessage?: string;
};

const RequestAdditionalDocumentsModal = <T extends string>({
  isOpen,
  onClose,
  title = 'Request Additional Documents',
  description = 'Select the documents you require from the customer from the list below',
  options,
  formatLabel = snakeToTitleCase,
  onSubmit,
  successMessage = 'Documents request successfully submitted.',
}: RequestAdditionalDocumentsModalProps<T>) => {
  const [selected, setSelected] = useState<T[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected([]);
    }
  }, [isOpen]);

  const handleCheckboxChange = (option: T) => {
    setSelected((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option],
    );
  };

  const handleSelectAll = () => setSelected([...options]);

  const handleClearSelection = () => setSelected([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected.length < 1) {
      toast.warn('You must select at least one document');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(selected);
      toast.success(successMessage);
      onClose();
    } catch (error) {
      logger.error('Error requesting additional documents:', error);
      toast.error(
        'Failed to request additional documents: ' + getErrorMessage(error, ''),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-10/12 sm:w-10/12 md:w-7/12 lg:w-4/12"
      // Not closable while the request is in flight.
      closeOnOutsideClick={!isSubmitting}
      hideX={isSubmitting}
      title={title}
      titleSize="text-sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            colorScheme="cyan"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="request-additional-documents-form"
            colorScheme="cyan"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            disabled={selected.length < 1}
          >
            Submit
          </Button>
        </div>
      }
    >
      <form id="request-additional-documents-form" onSubmit={handleSubmit}>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>

        {/* Count + bulk actions, so a long checklist is manageable at a glance. */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {selected.length} of {options.length} selected
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs font-medium text-cyan-700 hover:underline dark:text-cyan-500"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={handleClearSelection}
              disabled={selected.length === 0}
              className="text-xs font-medium text-gray-500 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400"
            >
              Clear
            </button>
          </div>
        </div>

        {/* No inner scroll here on purpose: the modal body is already the
            single scroll region, so an extra max-height/overflow on this list
            would stack a second scrollbar next to the body's. */}
        <div className="mt-2 space-y-2">
          {options.map((option) => {
            const isSelected = selected.includes(option);
            return (
              <CheckBox
                key={option}
                id={option}
                label={formatLabel(option)}
                type="checkbox"
                checked={isSelected}
                onChange={() => handleCheckboxChange(option)}
                labelClassName={cn(
                  'mb-0 w-full rounded-lg border px-3 py-2.5 transition-colors duration-150',
                  isSelected
                    ? 'border-cyan-600 bg-cyan-500/10'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-dark-borderHeavy dark:hover:bg-white/5',
                )}
              />
            );
          })}
        </div>
      </form>
    </Modal>
  );
};

export default RequestAdditionalDocumentsModal;
